import { useQuery } from "@tanstack/react-query";
import { admissionTypesApi, personTypesApi, insuranceSchemesApi } from "@/api/endpoints";

/** A dimension master row, reduced to what a picker needs. */
export interface BillingDimensionOption {
  id: string;
  code: string;
  name: string;
}

export interface BillingDimensions {
  admissionTypes: BillingDimensionOption[];
  personTypes: BillingDimensionOption[];
  insuranceSchemes: BillingDimensionOption[];
  isLoading: boolean;
}

/**
 * The three masters that decide which charge-master row prices a bill: admission type, person
 * type and insurance scheme.
 *
 * Small, rarely-edited lists, so they are fetched whole and cached for five minutes rather than
 * searched. Only active rows — an inactive scheme is one nobody should be billing under, and the
 * server rejects it anyway (BillingDimensionGuard).
 */
export function useBillingDimensions(enabled = true): BillingDimensions {
  const common = { staleTime: 5 * 60_000, enabled };

  const admission = useQuery({
    queryKey: ["admission-types", "options"],
    queryFn: () => admissionTypesApi.list({ isActive: true, pageSize: 200 }),
    ...common,
  });
  const person = useQuery({
    queryKey: ["person-types", "options"],
    queryFn: () => personTypesApi.list({ isActive: true, pageSize: 200 }),
    ...common,
  });
  const insurance = useQuery({
    queryKey: ["insurance-schemes", "options"],
    queryFn: () => insuranceSchemesApi.list({ isActive: true, pageSize: 200 }),
    ...common,
  });

  return {
    admissionTypes: admission.data?.items ?? [],
    personTypes: person.data?.items ?? [],
    insuranceSchemes: insurance.data?.items ?? [],
    isLoading: admission.isLoading || person.isLoading || insurance.isLoading,
  };
}
